import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

export default function Header() {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.title =
      "Majorah – Advanced Investing, Analytics & Research Platform";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }

    meta.content =
      "Majorah is a next-generation investing and analytics platform focused on asymmetric opportunities, advanced technology, and macro-aware portfolio construction.";

    if (!document.getElementById("majorah-jsonld")) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "majorah-jsonld";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Majorah",
        url: "https://majorah.com",
        logo: "https://majorah.com/logo.png",
      });
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const nav = [
    { label: "Stock Screener", to: "/StockScreener" },
    { label: "Portfolio Builder", to: "/diversification" },
    { label: "Macro Map", to: "/clusters" },
    { label: "Daily Returns", to: "/macro_dimension_scores" },
    { label: "Options Lab", to: "/OptionsPricer" },
    { label: "Rankings", to: "/StockRankings" },
    { label: "Insider Trades", to: "/InsiderTrading" },
    { label: "Fund Holdings", to: "/smartmoney" },
    { label: "Compare Stocks", to: "/compare" },
    { label: "Simulator", to: "/model" },
    { label: "Map", to: "/theme_map" },
    { label: "Allan Porter Holdings", to: "/holdings" },
    { label: "Allan Porter ETFs", to: "/custom-etfs" },
    { label: "Disclaimer", to: "/disclaimer" },
  ];

  return (
    <>
      <style>{`
        .majorah-header {
          display: flex;
          align-items: flex-start;
          padding: 12px 24px;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          background: #fff;
          z-index: 1000;
        }

        .majorah-logo {
          height: 80px;
          width: auto;
        }

        .majorah-nav {
          margin-left: auto;
          display: flex;
          gap: 18px;
          align-items: flex-end;
          padding-top: 36px;
          font-family: system-ui;
        }

        .majorah-mobile-toggle {
          display: none;
        }

        .majorah-mobile-nav {
          display: none;
        }

        @media (max-width: 768px) {
          .majorah-header {
            align-items: center;
            padding: 8px 10px;
            flex-wrap: wrap;
            max-height: 100vh;
            overflow: visible;
          }

          .majorah-logo {
            height: 44px;
          }

          .majorah-nav {
            display: none;
          }

          .majorah-mobile-toggle {
            margin-left: auto;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #e2e8f0;
            background: #f8fafc;
            color: #0f172a;
            border-radius: 10px;
            padding: 7px 10px;
            font-size: 12px;
            font-weight: 850;
            cursor: pointer;
          }

          .majorah-mobile-nav {
            display: block;
            width: 100%;
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.22s ease;
          }

          .majorah-mobile-nav.open {
            max-height: calc(100vh - 68px);
            overflow-y: auto;
            overflow-x: hidden;
            margin-top: 8px;
            padding-bottom: 10px;
            -webkit-overflow-scrolling: touch;
          }

          .majorah-mobile-nav-inner {
            display: grid;
            grid-template-columns: 1fr;
            gap: 6px;
            padding: 8px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
          }

          .majorah-mobile-nav.open::-webkit-scrollbar {
            width: 6px;
          }

          .majorah-mobile-nav.open::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 999px;
          }
        }
      `}</style>

      <header className="majorah-header">
        <Link to="/" style={{ display: "flex", alignItems: "center" }}>
          <img src={logo} alt="Majorah" className="majorah-logo" />
        </Link>

        <nav className="majorah-nav">
          {nav.map((n) => {
            const active = pathname.startsWith(n.to);

            return (
              <Link key={n.to} to={n.to} style={getNavLinkStyle(active)}>
                {n.label}
              </Link>
            );
          })}
        </nav>

        <button
          className="majorah-mobile-toggle"
          onClick={() => setMobileOpen((prev) => !prev)}
          type="button"
        >
          {mobileOpen ? "Close" : "Menu"} ☰
        </button>

        <div className={`majorah-mobile-nav ${mobileOpen ? "open" : ""}`}>
          <div className="majorah-mobile-nav-inner">
            {nav.map((n) => {
              const active = pathname.startsWith(n.to);

              return (
                <Link key={n.to} to={n.to} style={getMobileNavLinkStyle(active)}>
                  {n.label}
                </Link>
              );
            })}
          </div>
        </div>
      </header>
    </>
  );
}

function getNavLinkStyle(active) {
  return {
    textDecoration: "none",
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? "#000" : "#666",
    borderBottom: active ? "2px solid #000" : "2px solid transparent",
    paddingBottom: 6,
    whiteSpace: "nowrap",
  };
}

function getMobileNavLinkStyle(active) {
  return {
    textDecoration: "none",
    fontSize: 12,
    fontWeight: active ? 850 : 650,
    color: active ? "#0f172a" : "#475569",
    background: active ? "#e0f2fe" : "#ffffff",
    border: active ? "1px solid #7dd3fc" : "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "8px 10px",
    lineHeight: 1.15,
  };
}